// src/lib/taxonomy/capability-synonyms.ts
// Capability synonym mappings for matching variations of the same concept
// Maps various terms to canonical capability IDs

import { CapabilitySynonym } from './types';

export const CAPABILITY_SYNONYMS: CapabilitySynonym[] = [
  // ============================================================
  // SOFTWARE DEVELOPMENT
  // ============================================================
  {
    canonicalId: 'software_development',
    canonicalName: 'Software Development',
    category: 'technology',
    description: 'Building and maintaining software applications',
    synonyms: [
      'software engineering',
      'application development',
      'app development',
      'programming',
      'coding',
      'software design',
      'systems development',
      'custom software',
      'bespoke software',
      'software creation'
    ]
  },
  {
    canonicalId: 'web_development',
    canonicalName: 'Web Development',
    category: 'technology',
    description: 'Building websites and web applications',
    synonyms: [
      'web design',
      'website development',
      'web application development',
      'web programming',
      'internet development',
      'web services development',
      'web app development'
    ]
  },
  {
    canonicalId: 'frontend_development',
    canonicalName: 'Frontend Development',
    category: 'technology',
    description: 'Building user interfaces and client-side applications',
    synonyms: [
      'front-end development',
      'front end development',
      'UI development',
      'user interface development',
      'client-side development',
      'presentation layer'
    ]
  },
  {
    canonicalId: 'backend_development',
    canonicalName: 'Backend Development',
    category: 'technology',
    description: 'Building server-side applications and APIs',
    synonyms: [
      'back-end development',
      'back end development',
      'server-side development',
      'server development',
      'API development',
      'service development'
    ]
  },
  {
    canonicalId: 'mobile_development',
    canonicalName: 'Mobile Application Development',
    category: 'technology',
    description: 'Building applications for mobile devices',
    synonyms: [
      'mobile app development',
      'mobile development',
      'iOS development',
      'Android development',
      'smartphone app development',
      'mobile programming'
    ]
  },

  // ============================================================
  // CLOUD & INFRASTRUCTURE
  // ============================================================
  {
    canonicalId: 'cloud_computing',
    canonicalName: 'Cloud Computing',
    category: 'technology',
    description: 'Delivering computing services over the cloud',
    synonyms: [
      'cloud services',
      'cloud technology',
      'cloud infrastructure',
      'cloud solutions',
      'cloud platforms',
      'IaaS',
      'PaaS',
      'SaaS',
      'cloud hosting'
    ]
  },
  {
    canonicalId: 'cloud_migration',
    canonicalName: 'Cloud Migration',
    category: 'technology',
    description: 'Moving applications and data to the cloud',
    synonyms: [
      'cloud transition',
      'cloud modernization',
      'cloud transformation',
      'migration to cloud',
      'cloud adoption',
      'lift and shift',
      'cloud move'
    ]
  },
  {
    canonicalId: 'devops',
    canonicalName: 'DevOps',
    category: 'technology',
    description: 'Practices combining development and operations',
    synonyms: [
      'dev ops',
      'development operations',
      'DevSecOps',
      'continuous delivery',
      'continuous deployment',
      'CI/CD',
      'CICD',
      'pipeline automation'
    ]
  },
  {
    canonicalId: 'containerization',
    canonicalName: 'Containerization',
    category: 'technology',
    description: 'Packaging applications in containers',
    synonyms: [
      'container technology',
      'Docker',
      'container orchestration',
      'Kubernetes',
      'K8s',
      'container deployment',
      'microservices containers'
    ]
  },

  // ============================================================
  // CYBERSECURITY
  // ============================================================
  {
    canonicalId: 'cybersecurity',
    canonicalName: 'Cybersecurity',
    category: 'security',
    description: 'Protecting systems and data from cyber threats',
    synonyms: [
      'cyber security',
      'information security',
      'infosec',
      'IT security',
      'computer security',
      'digital security',
      'network security',
      'security services'
    ]
  },
  {
    canonicalId: 'vulnerability_assessment',
    canonicalName: 'Vulnerability Assessment',
    category: 'security',
    description: 'Identifying security vulnerabilities in systems',
    synonyms: [
      'vulnerability scanning',
      'security scanning',
      'vulnerability analysis',
      'security assessment',
      'security testing',
      'vuln assessment',
      'vulnerability management'
    ]
  },
  {
    canonicalId: 'penetration_testing',
    canonicalName: 'Penetration Testing',
    category: 'security',
    description: 'Simulating attacks to test security',
    synonyms: [
      'pen testing',
      'pentest',
      'ethical hacking',
      'red team',
      'security penetration',
      'offensive security',
      'attack simulation'
    ]
  },
  {
    canonicalId: 'incident_response',
    canonicalName: 'Incident Response',
    category: 'security',
    description: 'Responding to and managing security incidents',
    synonyms: [
      'security incident response',
      'IR',
      'breach response',
      'incident handling',
      'incident management',
      'security incident management',
      'cyber incident response'
    ]
  },
  {
    canonicalId: 'security_operations',
    canonicalName: 'Security Operations',
    category: 'security',
    description: 'Operating and monitoring security systems',
    synonyms: [
      'SecOps',
      'security operations center',
      'SOC',
      'security monitoring',
      '24x7 security',
      'continuous monitoring',
      'security watch'
    ]
  },

  // ============================================================
  // DATA & ANALYTICS
  // ============================================================
  {
    canonicalId: 'data_analytics',
    canonicalName: 'Data Analytics',
    category: 'data',
    description: 'Analyzing data to derive insights',
    synonyms: [
      'data analysis',
      'analytics',
      'business analytics',
      'advanced analytics',
      'data insights',
      'analytical services',
      'data science'
    ]
  },
  {
    canonicalId: 'business_intelligence',
    canonicalName: 'Business Intelligence',
    category: 'data',
    description: 'Tools and practices for analyzing business data',
    synonyms: [
      'BI',
      'business analytics',
      'reporting and analytics',
      'dashboards',
      'data visualization',
      'executive dashboards',
      'management reporting'
    ]
  },
  {
    canonicalId: 'big_data',
    canonicalName: 'Big Data',
    category: 'data',
    description: 'Processing and analyzing large data sets',
    synonyms: [
      'large-scale data',
      'data at scale',
      'massive data',
      'data lakes',
      'data warehousing',
      'enterprise data',
      'high-volume data'
    ]
  },
  {
    canonicalId: 'machine_learning',
    canonicalName: 'Machine Learning',
    category: 'data',
    description: 'Building systems that learn from data',
    synonyms: [
      'ML',
      'artificial intelligence',
      'AI',
      'predictive analytics',
      'predictive modeling',
      'AI/ML',
      'intelligent systems',
      'cognitive computing'
    ]
  },

  // ============================================================
  // PROJECT & PROGRAM MANAGEMENT
  // ============================================================
  {
    canonicalId: 'project_management',
    canonicalName: 'Project Management',
    category: 'management',
    description: 'Planning and executing projects',
    synonyms: [
      'PM',
      'project delivery',
      'project execution',
      'project planning',
      'project oversight',
      'project coordination',
      'project control'
    ]
  },
  {
    canonicalId: 'program_management',
    canonicalName: 'Program Management',
    category: 'management',
    description: 'Managing multiple related projects',
    synonyms: [
      'program delivery',
      'program execution',
      'portfolio management',
      'multi-project management',
      'enterprise program management',
      'large program management'
    ]
  },
  {
    canonicalId: 'agile_methodologies',
    canonicalName: 'Agile Methodologies',
    category: 'management',
    description: 'Iterative and incremental development approaches',
    synonyms: [
      'agile',
      'agile development',
      'scrum',
      'kanban',
      'lean',
      'iterative development',
      'sprint-based',
      'agile project management'
    ]
  },
  {
    canonicalId: 'change_management',
    canonicalName: 'Change Management',
    category: 'management',
    description: 'Managing organizational and system changes',
    synonyms: [
      'organizational change',
      'change control',
      'transformation management',
      'OCM',
      'transition management',
      'change implementation'
    ]
  },

  // ============================================================
  // FEDERAL/GOVERNMENT SPECIFIC
  // ============================================================
  {
    canonicalId: 'federal_contracting',
    canonicalName: 'Federal Contracting',
    category: 'federal',
    description: 'Experience with federal government contracts',
    synonyms: [
      'government contracting',
      'federal contracts',
      'gov contracting',
      'federal acquisition',
      'government work',
      'public sector',
      'federal experience'
    ]
  },
  {
    canonicalId: 'cleared_personnel',
    canonicalName: 'Cleared Personnel',
    category: 'federal',
    description: 'Personnel with security clearances',
    synonyms: [
      'cleared staff',
      'security cleared',
      'clearance holders',
      'cleared workforce',
      'TS cleared',
      'secret cleared',
      'cleared employees'
    ]
  },
  {
    canonicalId: 'fedramp_compliance',
    canonicalName: 'FedRAMP Compliance',
    category: 'federal',
    description: 'Federal Risk and Authorization Management Program compliance',
    synonyms: [
      'FedRAMP',
      'FedRAMP authorized',
      'FedRAMP certification',
      'federal cloud compliance',
      'FedRAMP ATO',
      'FedRAMP ready'
    ]
  },
  {
    canonicalId: 'fisma_compliance',
    canonicalName: 'FISMA Compliance',
    category: 'federal',
    description: 'Federal Information Security Management Act compliance',
    synonyms: [
      'FISMA',
      'federal security compliance',
      'NIST compliance',
      'federal information security',
      'FISMA authorization'
    ]
  },

  // ============================================================
  // IT SERVICE MANAGEMENT
  // ============================================================
  {
    canonicalId: 'it_service_management',
    canonicalName: 'IT Service Management',
    category: 'it_services',
    description: 'Managing IT services and operations',
    synonyms: [
      'ITSM',
      'service management',
      'IT operations',
      'service desk',
      'help desk',
      'IT support',
      'technical support',
      'service delivery'
    ]
  },
  {
    canonicalId: 'system_administration',
    canonicalName: 'System Administration',
    category: 'it_services',
    description: 'Managing and maintaining computer systems',
    synonyms: [
      'sys admin',
      'sysadmin',
      'server administration',
      'infrastructure management',
      'server management',
      'system management'
    ]
  },
  {
    canonicalId: 'network_engineering',
    canonicalName: 'Network Engineering',
    category: 'it_services',
    description: 'Designing and managing network infrastructure',
    synonyms: [
      'network administration',
      'network management',
      'network design',
      'network architecture',
      'networking',
      'network infrastructure'
    ]
  },
  {
    canonicalId: 'database_administration',
    canonicalName: 'Database Administration',
    category: 'it_services',
    description: 'Managing and maintaining databases',
    synonyms: [
      'DBA',
      'database management',
      'DB administration',
      'data administration',
      'database operations',
      'database support'
    ]
  },

  // ============================================================
  // CONSULTING & ADVISORY
  // ============================================================
  {
    canonicalId: 'management_consulting',
    canonicalName: 'Management Consulting',
    category: 'consulting',
    description: 'Providing expert business advice',
    synonyms: [
      'consulting services',
      'business consulting',
      'advisory services',
      'strategic consulting',
      'professional consulting',
      'expert consulting'
    ]
  },
  {
    canonicalId: 'technical_consulting',
    canonicalName: 'Technical Consulting',
    category: 'consulting',
    description: 'Providing expert technical advice',
    synonyms: [
      'technology consulting',
      'IT consulting',
      'tech advisory',
      'technical advisory',
      'technology advisory',
      'technical guidance'
    ]
  },
  {
    canonicalId: 'process_improvement',
    canonicalName: 'Process Improvement',
    category: 'consulting',
    description: 'Improving business processes',
    synonyms: [
      'business process improvement',
      'BPI',
      'process optimization',
      'process reengineering',
      'lean process',
      'efficiency improvement',
      'operational improvement'
    ]
  },

  // ============================================================
  // HEALTHCARE
  // ============================================================
  {
    canonicalId: 'healthcare_it',
    canonicalName: 'Healthcare IT',
    category: 'healthcare',
    description: 'Information technology for healthcare',
    synonyms: [
      'health IT',
      'HIT',
      'medical IT',
      'healthcare technology',
      'health information technology',
      'clinical IT',
      'medical informatics'
    ]
  },
  {
    canonicalId: 'electronic_health_records',
    canonicalName: 'Electronic Health Records',
    category: 'healthcare',
    description: 'Digital health record systems',
    synonyms: [
      'EHR',
      'EMR',
      'electronic medical records',
      'health records',
      'patient records',
      'clinical records',
      'digital health records'
    ]
  },
  {
    canonicalId: 'hipaa_compliance',
    canonicalName: 'HIPAA Compliance',
    category: 'healthcare',
    description: 'Health Insurance Portability and Accountability Act compliance',
    synonyms: [
      'HIPAA',
      'health data compliance',
      'healthcare compliance',
      'patient data protection',
      'health information privacy',
      'PHI protection'
    ]
  },

  // ============================================================
  // FACILITIES & CONSTRUCTION
  // ============================================================
  {
    canonicalId: 'facilities_management',
    canonicalName: 'Facilities Management',
    category: 'facilities',
    description: 'Managing and maintaining physical facilities',
    synonyms: [
      'facility management',
      'building management',
      'property management',
      'facilities operations',
      'building operations',
      'O&M'
    ]
  },
  {
    canonicalId: 'construction_management',
    canonicalName: 'Construction Management',
    category: 'facilities',
    description: 'Managing construction projects',
    synonyms: [
      'construction oversight',
      'CM',
      'construction supervision',
      'building construction',
      'project construction',
      'construction services'
    ]
  }
];

/**
 * Find the canonical capability for a given term
 */
export function findCanonicalCapability(term: string): CapabilitySynonym | undefined {
  const normalizedTerm = term.toLowerCase().trim();

  return CAPABILITY_SYNONYMS.find(cap =>
    cap.canonicalId === normalizedTerm ||
    cap.canonicalName.toLowerCase() === normalizedTerm ||
    cap.synonyms.some(syn => syn.toLowerCase() === normalizedTerm)
  );
}

/**
 * Search capabilities by term (partial match)
 */
export function searchCapabilities(term: string): CapabilitySynonym[] {
  const normalizedTerm = term.toLowerCase().trim();

  return CAPABILITY_SYNONYMS.filter(cap =>
    cap.canonicalId.includes(normalizedTerm) ||
    cap.canonicalName.toLowerCase().includes(normalizedTerm) ||
    cap.description.toLowerCase().includes(normalizedTerm) ||
    cap.synonyms.some(syn => syn.toLowerCase().includes(normalizedTerm))
  );
}

/**
 * Get capabilities by category
 */
export function getCapabilitiesByCategory(category: string): CapabilitySynonym[] {
  return CAPABILITY_SYNONYMS.filter(cap => cap.category === category);
}

/**
 * Get all categories
 */
export function getAllCapabilityCategories(): string[] {
  const categories = new Set<string>();
  for (const cap of CAPABILITY_SYNONYMS) {
    categories.add(cap.category);
  }
  return Array.from(categories).sort();
}

/**
 * Normalize a term to its canonical ID
 * Returns the original term if no match found
 */
export function normalizeCapabilityTerm(term: string): string {
  const canonical = findCanonicalCapability(term);
  return canonical?.canonicalId || term.toLowerCase().replace(/\s+/g, '_');
}

/**
 * Get all synonyms for a capability
 */
export function getSynonymsForCapability(canonicalId: string): string[] {
  const cap = CAPABILITY_SYNONYMS.find(c => c.canonicalId === canonicalId);
  if (!cap) return [];
  return [cap.canonicalName, ...cap.synonyms];
}

/**
 * Check if two terms refer to the same capability
 */
export function areCapabilitiesEquivalent(term1: string, term2: string): boolean {
  const cap1 = findCanonicalCapability(term1);
  const cap2 = findCanonicalCapability(term2);

  if (cap1 && cap2) {
    return cap1.canonicalId === cap2.canonicalId;
  }

  // Fall back to direct comparison
  return term1.toLowerCase() === term2.toLowerCase();
}
